import {observable, ObservableMap, makeObservable, action, computed} from 'mobx';
import {languageEnum, procedureNodeTypeEnum} from "../serverClientShared/sharedEnums";
import {typeOfError} from "../Components/Editor/LanguageManagement/TranaslatingSteps/TranslationErrorDialog";
import {projectStore} from "../index";


export class EditorStore {
    constructor() {
        makeObservable(this, {
            connections: observable,
            nodes: observable,
            changesHistory: observable,
            historyIndex: observable,
            latestState: observable,
            connectingHandle: observable,
            isConnecting: observable,
            selectedElements: observable,
            isDragStart: observable,
            disableSelectUpdate: observable,
            oldNodesArray: observable,
            oldConnectionsArray: observable,
            enableUpdatePreviousState: observable,
            isTransIncomplete: observable,
            editorRoomUsers: observable,
            sentInitialUser: observable,
            disableChanges: observable,
            lastUndoRedoTimeStamp: observable,
            showConnectionLabels: observable,
            headerX: observable,
            headerY: observable,
            nodesToTranslate: observable,
            isSkipText: observable,
            languagesToTranslate: observable,
            isTranslationOptionsDialogOpen: observable,
            isTranslationProgressDialogOpen: observable,
            isTranslationErrorDialogOpen: observable,
            isTranslationSuccessDialogOpen: observable,
            translateSelectedNodes: observable,
            errorType: observable,
            errorInfo: observable,
            cancelTranslation: observable,
            openedTabs: observable,
            selectInnerProcedureDialogOpen: observable,
            refreshTrigger: observable,
            autoArrangeTrigger: observable,
            selectionUsers: computed,
            currentSelectedNodeIds: computed,
            getNodeUser: action,
            getOtherUserHasNodeSelected: action,
            reset: action,
            generateUpdatedNodes: action,
            resetChangeHistory: action,
            applyAINodeTranslation: action,
            removeNodesThatShouldNotBeTranslated: action,
            checkIncompleteTranslations: action,
        });
    }

    connections = new ObservableMap();
    nodes = new ObservableMap();
    changesHistory = [];
    selectedElements = [];
    historyIndex = 0;
    latestState = {};
    connectingHandle = null;
    isConnecting = false;
    isDragStart = false;
    disableSelectUpdate = false;
    draggedSelectedConnections = new Map();
    selectionPreviousPosition = {};
    oldNodesArray = [];
    oldConnectionsArray = [];
    enableUpdatePreviousState = true;
    editorRoomUsers = {
        usedColors: new Set(),
        currentUsers: new Map()
    }
    openedTabs = [];
    sentInitialUser = false;
    disableChanges = true;
    lastUndoRedoTimeStamp = Date.now();
    showConnectionLabels = true;
    isTransIncomplete = null;
    headerX = 0;
    headerY = 0;
    isTranslationOptionsDialogOpen = false;
    isTranslationProgressDialogOpen = false;
    isTranslationErrorDialogOpen = false;
    isTranslationSuccessDialogOpen = false;
    nodesToTranslate = new ObservableMap();
    isSkipText = false;
    languagesToTranslate = ['ENGLISH_US'];
    errorType = typeOfError.SOMETHING_WENT_WRONG
    cancelTranslation = false;
    errorInfo = '';
    translateSelectedNodes = false;
    selectInnerProcedureDialogOpen = false;
    refreshTrigger = false;
    autoArrangeTrigger = 0;

    get selectionUsers() {
        const selectionUsers = new Map();
        this.editorRoomUsers.currentUsers.forEach((user, userId) => {
            user.selectedNodeIds?.forEach(nodeId => {
                selectionUsers.set(nodeId, {
                    userId,
                    selectionColor: user.avatarColor
                });
            });
        });
        return selectionUsers
    }

    get currentSelectedNodeIds() {
        const selectedNodeIds = [];
        this.nodes.forEach((node, nodeId) => {
            if (node.selected) {
                selectedNodeIds.push(nodeId)
            }
        });
        return selectedNodeIds
    }

    getNodeUser = nodeId => this.selectionUsers.get(nodeId);

    getOtherUserHasNodeSelected = nodeId => !!this.getNodeUser(nodeId);

    resetChangeHistory = () => {
        this.changesHistory = [];
        this.historyIndex = 0;
    }

    removeNodesThatShouldNotBeTranslated = (nodesToTranslate) => {
        const nodesMap = new ObservableMap(nodesToTranslate)
        for (const proxyNode of nodesMap) {
            const nodeDataProxy = proxyNode[1].data.nodeData;

            if (nodeDataProxy?.NodeType === procedureNodeTypeEnum.START || nodeDataProxy?.IsPlatformSwitch) {
                nodesMap.delete(proxyNode[0]);
            }
        }

        return nodesMap;
    }

    reset = () => {
        this.changesHistory = [];
        this.selectedElements = [];
        this.connections = new ObservableMap();
        this.nodes = new ObservableMap();
        this.historyIndex = 0;
        this.latestState = {};
        this.isDragStart = false;
        this.disableSelectUpdate = false;
        this.draggedSelectedConnections = new Map();
        this.selectionPreviousPosition = {};
        this.oldNodesArray = [];
        this.oldConnectionsArray = [];
        this.enableUpdatePreviousState = true;
        this.editorRoomUsers = {
            usedColors: new Set(),
            currentUsers: new Map()
        }
        this.sentInitialUser = false;
        this.disableChanges = true;
        this.showConnectionLabels = true;
        this.headerX = 0;
        this.headerY = 0;
        this.isTranslationOptionsDialogOpen = false;
        this.isTranslationProgressDialogOpen = false;
        this.isTranslationErrorDialogOpen = false;
        this.isTranslationSuccessDialogOpen = false;
    }

    checkNodesWithText = (languages, allNodesToTranslate = []) => {
        let nodesWithText = new Map()

        const nodesForTranslation = this.translateSelectedNodes ? allNodesToTranslate : this.nodes;

        nodesForTranslation.forEach(node => {
            languages.forEach(language => {
                const langCode = languageEnum[language];
                const nodeData = node.data.nodeData;
                if (
                    (nodeData.StepTextMulti && nodeData.StepTextMulti[langCode]) ||
                    (nodeData.StepHeaderMulti && nodeData.StepHeaderMulti[langCode]) ||
                    (nodeData.ConformationTextMulti && nodeData.ConformationTextMulti[langCode]) ||
                    (nodeData.CustomTextToSpeechMulti && nodeData.CustomTextToSpeechMulti[langCode])) {
                    nodesWithText.set(node.id, node);
                } else if (nodeData.ConnectionsData) {
                    nodeData.ConnectionsData.forEach(connection => {
                        if (connection.ConnectionNameMulti && connection.ConnectionNameMulti[langCode]) {
                            nodesWithText.set(node.id, node);
                        }
                    });
                }
            });
        });
        return nodesWithText;
    }

    getLanguages = (langKey, availableNodeLanguages) => {
        const key = Object.entries(languageEnum).find(([_, value]) => value === langKey)?.[0];
        return key ? [key] : availableNodeLanguages;
    }

    deepCloneWithoutProxies = (obj) => {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.deepCloneWithoutProxies(item));
        }

        const newObj = {};
        const ownKeys = Reflect.ownKeys(obj);
        for (const key of ownKeys) {
            const value = obj[key];
            newObj[key] = this.deepCloneWithoutProxies(value);
        }
        return newObj;
    }

    applyAINodeTranslation = async (translatedNestedNodes) => {
        translatedNestedNodes.forEach(translatedNode => {
            const nodeId = translatedNode.Identifier;
            const existingNode = this.nodes.get(nodeId);

            if (existingNode) {
                const updatedNodeData = {
                    ...existingNode.data,
                    nodeData: {
                        ...existingNode.data.nodeData,
                        ...translatedNode
                    }
                };

                this.nodes.set(nodeId, {
                    ...existingNode,
                    data: updatedNodeData
                });
            }

            translatedNode?.ConnectionsData?.forEach(connection => {
                const connectionId = connection.Handle;
                const existingConnection = this.connections.get(nodeId)?.data_?.get(connectionId)?.value_;

                if (existingConnection) {
                    existingConnection.data.ConnectionNameMulti = {
                        ...existingConnection.data.ConnectionNameMulti,
                        ...connection.ConnectionNameMulti
                    };
                }

                this.connections = new ObservableMap(this.connections);
            });

        });

        return true;
    }

    addParamsToInteraction = (interaction) => {
        if (!interaction.interactionParams || !interaction.interactionParamList) {
            const interactionParams = new Map()
            const interactionParamList = ['Text', 'IsReverse', 'RequireInteraction'];
            interactionParamList.forEach(param => {
                interactionParams.set(param, param === "Text" ? "" : (interaction.interactionParams?.get(param) || false));
            });
            return {
                ...interaction,
                interactionParamList,
                interactionParams
            }

        } else {
            return interaction
        }
    }

    applyInappNodeChanges = async (existingChanges, allInteractions) => {
        existingChanges.forEach(changedNode => {
            const nodeId = changedNode?.Identifier;
            if (!nodeId) {
                return;
            }

            const allInteractionsWithParams = allInteractions
                .map(interaction => this.addParamsToInteraction(interaction))
                .filter(interaction => interaction.InteractiveStepID === changedNode.NodeInteractionItems[0].InteractionId)

            const existingNode = this.nodes.get(nodeId);
            if (existingNode && projectStore.currentProject) {
                    existingNode.data.interactions = allInteractionsWithParams
                    existingNode.data.NodeInteractionItems = {...changedNode.NodeInteractionItems, interactionParamList: ['Text', 'IsReverse', 'RequireInteraction']};
                    existingNode.data.nodeData.NodeInteractionItems = {...changedNode.NodeInteractionItems, interactionParamList: ['Text', 'IsReverse', 'RequireInteraction']};
                    this.nodes.set(nodeId, existingNode);
            }
        });

        return this.nodes;
    }

    checkIncompleteTranslations = (translatedNestedNodes, targetLangCodes, defaultLang) => {
        let uncompleted = false
        for (const translatedNode of translatedNestedNodes) {
            const nodeId = translatedNode.Identifier;
            const existingNode = this.nodes.get(nodeId);

            if (existingNode) {
                const node = existingNode.data.nodeData;

                if (node?.StepText) {
                    if (!node.StepTextMulti) {
                        uncompleted = true;
                        break;
                    }
                    for (const targetLangCode of targetLangCodes) {
                        if (!node.StepTextMulti || !node.StepTextMulti[targetLangCode]) {
                            uncompleted = true;
                            break;
                        }
                    }
                }

                if (node?.StepHeader) {
                    if (!node.StepHeaderMulti) {
                        uncompleted = true;
                        break;
                    }
                    for (const targetLangCode of targetLangCodes) {
                        if (!node.StepHeaderMulti || !node.StepHeaderMulti[targetLangCode]) {
                            uncompleted = true;
                            break;
                        }
                    }

                }

                if (node.CustomTextToSpeechMulti && node.CustomTextToSpeechMulti[defaultLang]) {
                    for (const targetLangCode of targetLangCodes) {
                        if (!node.CustomTextToSpeechMulti || !node.CustomTextToSpeechMulti[targetLangCode]) {
                            uncompleted = true;
                            break;
                        }
                    }
                }

                if (node?.ConformationText) {
                    if (!node.ConfirmationTextMulti) {
                        uncompleted = true;
                        break;
                    }
                    for (const targetLangCode of targetLangCodes) {
                        if (!node.ConfirmationTextMulti[targetLangCode] || !node.ConfirmationTextMulti[targetLangCode].length) {
                            uncompleted = true;
                            break;
                        }
                    }
                }

                if (node.ConnectionsData) {
                    for (const existingConnection of node.ConnectionsData) {
                        if (existingConnection && existingConnection?.ConnectionName) {
                            for (const targetLangCode of targetLangCodes) {
                                if (!existingConnection?.ConnectionNameMulti || !existingConnection.ConnectionNameMulti[targetLangCode]) {
                                    uncompleted = true;
                                }
                            }
                        }
                    }
                }
            }
        }

        this.isTransIncomplete = uncompleted;
    }

    generateUpdatedNodes = currentNodes => {
        const updatedNodes = [];
        currentNodes.forEach(node => {
            if (node.data.nodeData.NodeType === procedureNodeTypeEnum.CONNECTION_POINT) {
                return
            }
            const updatedNode = node.data.nodeData;
            updatedNode.ThisNodePosition = node.position;
            if (!!node.data?.interactions?.length || node.data?.interactions?.length === 0) {
                updatedNode.NodeInteractionItems = [];
                node.data.interactions.forEach(interaction => {
                    const interactionItem = {
                        InteractionId: interaction.InteractiveStepID
                    }
                    if (interaction.interactionParams) {
                        const paramObj = {};
                        interaction.interactionParams.forEach((param, paramKey) => paramObj[paramKey] = param);
                        interactionItem.InteractionParams = JSON.stringify(paramObj);

                    }
                    updatedNode.NodeInteractionItems.push(interactionItem);
                });
            }

            const nodeConnectionSourceMap = this.connections.get(node.id);

            const updatedConnections = [];
            if (!!node.data?.handles?.size) {
                node.data.handles.forEach(handle => {
                    let connection = nodeConnectionSourceMap?.get(handle);
                    if (!connection) {
                        updatedConnections.push({
                            ConnectedTo: "",
                            Handle: handle
                        });
                    } else {
                        const connectionData = connection.data || {};
                        connectionData.ConnectedTo = currentNodes.has(connectionData.finalTarget) ? connectionData.finalTarget : undefined;
                        connectionData.Handle = handle;
                        const updatedConnectionCoordinates = [];
                        if (!!connectionData.userModified) {
                            let sourceX, sourceSecondaryY, midX, targetSecondaryY, targetX;
                            connectionData.connectionPoints?.forEach(connectionNode => {
                                switch (connectionNode.data?.autoConnectionPointType) {
                                    case "sourceInitial":
                                        sourceX = connectionNode.position.x;
                                        sourceSecondaryY = connectionNode.position.y;
                                        break
                                    case "midLineHandle":
                                        midX = connectionNode.position.x;
                                        break
                                    case "targetFinal":
                                        targetSecondaryY = connectionNode.position.y;
                                        targetX = connectionNode.position.x;
                                        break
                                    default:
                                        break
                                }
                            })
                            updatedConnectionCoordinates.push(sourceX, sourceSecondaryY, midX, targetSecondaryY, targetX);
                        }
                        connectionData.ConnectionCoordinates = updatedConnectionCoordinates;
                        updatedConnections.push(connectionData);
                    }
                });
            }
            updatedNode.ConnectionsData = updatedConnections;
            updatedNodes.push(updatedNode);
        });
        return updatedNodes
    }
}
